import { useMemo, useState } from 'react'
import { useBlacklisted, useUnblacklist } from '@/api/queries'
import type { BlacklistedProxy } from '@/api/types'
import { DataTable, type Column } from '@/components/DataTable/DataTable'
import { Button, IconButton } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Icons } from '@/components/ui/Icons'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { ageShort, dateTime } from '@/lib/format'
import { matchesPos, type PosScope } from './blacklistByPos'

/** Every blacklisted IP, or only those of one POS when opened from a POS row. */
export function BlacklistDialog({ onClose, scope: initialScope }: { onClose: () => void; scope?: PosScope }) {
  const { data, isLoading, error } = useBlacklisted(true)
  const unblacklist = useUnblacklist()
  const toast = useToast()
  const [pending, setPending] = useState<BlacklistedProxy | null>(null)
  const [scope, setScope] = useState<PosScope | null>(initialScope ?? null)
  const rows = useMemo(() => (scope ? data?.filter((p) => matchesPos(p, scope.pos)) : data), [data, scope])

  const remove = async (proxy: BlacklistedProxy) => {
    setPending(null)
    try {
      await unblacklist.mutateAsync({ ip: proxy.ip, domain: proxy.domain })
      toast.notify(`${proxy.ip} removed from the ${proxy.domain} blacklist`, 'success')
    } catch (err) {
      toast.error(err, 'Could not remove the IP')
    }
  }

  const columns: Column<BlacklistedProxy>[] = [
    {
      key: 'ip',
      header: 'IP',
      render: (p) => (
        <span className="u-mono">
          {p.ip}
          {p.port ? `:${p.port}` : ''}
        </span>
      ),
    },
    { key: 'domain', header: 'Domain' },
    { key: 'channelName', header: 'Site', render: (p) => p.channelName ?? '–' },
    { key: 'source', header: 'Source', render: (p) => p.source ?? '–' },
    { key: 'blacklistedAt', header: 'Since', align: 'right', render: (p) => (p.blacklistedAt ? <span title={dateTime(p.blacklistedAt)}>{ageShort(p.blacklistedAt)}</span> : '–') },
    {
      key: 'actions',
      header: '',
      sortable: false,
      width: '48px',
      render: (p) => (
        <IconButton label="Remove from the blacklist" onClick={() => setPending(p)} disabled={unblacklist.isPending}>
          <Icons.Trash size={14} />
        </IconButton>
      ),
    },
  ]

  return (
    <Modal
      title={`Blacklisted IPs${scope ? ` · ${scope.label}` : ''}${rows ? ` (${rows.length})` : ''}`}
      onClose={onClose}
      size="xl"
      headerExtra={
        scope && (
          <Button size="sm" onClick={() => setScope(null)} title="Back to the IPs of every POS">
            Show every POS
          </Button>
        )
      }
    >
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(p) => `${p.ip}:${p.port}:${p.domain}`}
        loading={isLoading}
        error={error}
        searchable
        searchPlaceholder="Search an IP, domain or source"
        dense
        initialSort={{ key: 'blacklistedAt', direction: 'desc' }}
        copyText={(rows) => rows.map((r) => `${r.ip}\t${r.domain}`).join('\n')}
        emptyMessage={scope ? `No blacklisted IP for ${scope.label}` : 'No IP is blacklisted'}
      />
      {pending && (
        <ConfirmDialog
          title="Remove from the blacklist"
          message={`Allow ${pending.ip} on ${pending.domain} again? The proxy will be used right away.`}
          confirmLabel="Remove"
          danger
          onCancel={() => setPending(null)}
          onConfirm={() => void remove(pending)}
        />
      )}
    </Modal>
  )
}
